import React, { useCallback, useState } from 'react';
import styled from 'styled-components';
import ImpactComments from './ImpactComments';
import TrueImpactIconText from 'shared/design-system/components/TrueImpactIconText';
import TrueImpactCollapse from 'shared/design-system/components/TrueImpactCollapse';
import { faComment as faCommentSolid } from '@fortawesome/pro-solid-svg-icons';
import { faComment } from '@fortawesome/pro-light-svg-icons';
import { FeedbackCommentBodyWithIndex } from '../../../utils';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import { FeedbackCommentProps } from 'donor/portal/pages/FeedbackDashboard/Feedback/utils';

interface ShowIndicatorFeedbackProps {
  defaultOpen?: boolean;
  comments: FeedbackCommentBodyWithIndex[];
}

const StyledDiv = styled.div`
  .impact-model-comments {
    padding-bottom: 0;
  }
`;

const ShowIndicatorFeedback: React.FC<ShowIndicatorFeedbackProps> = ({
  comments,
  defaultOpen = true,
}) => {
  const [show, setShow] = useState<boolean>(defaultOpen);

  const toggleShow = useCallback(() => {
    setShow(s => !s);
  }, [setShow]);

  if (comments.length === 0) {
    return null;
  }

  return (
    <StyledDiv>
      <ShowIndicatorFeedbackButton
        show={show}
        feedbackText="Feedback"
        comments={comments}
        toggleShow={toggleShow}
      />
      <TrueImpactCollapse show={show}>
        <ImpactComments feedback={comments} className="impact-model-comments" />
      </TrueImpactCollapse>
    </StyledDiv>
  );
};

export default ShowIndicatorFeedback;

interface ShowIndicatorFeedbackButtonProps {
  show: boolean;
  feedbackText?: string;
  className?: string;
  reverseIcon?: boolean;
  nullFeedbackText?: string;
  allowTextSelection?: boolean;
  comments: (FeedbackCommentBodyWithIndex | FeedbackCommentProps)[];
  toggleShow: () => unknown;
}

const StyledIconText = styled(TrueImpactIconText)`
  display: flex;
  align-items: center;
  gap: ${SPACERS.extraTiny};
  margin-top: ${SPACERS.small};
`;

export const ShowIndicatorFeedbackButton: React.FC<ShowIndicatorFeedbackButtonProps> = ({
  show,
  comments,
  feedbackText,
  className,
  reverseIcon,
  nullFeedbackText,
  allowTextSelection = false,
  toggleShow,
}) => {
  const icon = reverseIcon
    ? show
      ? faCommentSolid
      : faComment
    : show
    ? faComment
    : faCommentSolid;
  const commentLabel = `${feedbackText ? `${feedbackText} ` : ''}Comment${
    comments.length > 1 ? 's' : ''
  }`;
  return (
    <StyledIconText
      variant="body-small"
      icon={icon}
      label={
        nullFeedbackText && comments.length === 0
          ? nullFeedbackText
          : show
          ? `Hide ${commentLabel}`
          : `${comments.length} ${commentLabel}`
      }
      onClick={toggleShow}
      className={className}
      allowTextSelection={allowTextSelection}
    />
  );
};
